import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { AppHelperService } from '../../app/services/app-helper.service';
import { AuthService } from '../../app/services/auth.service';

import { StudentBookService } from './student-book.service';
import { LastPageDataService } from './last-page-data.service';
import { DesktopEBookVMService } from 'modules/desktop/services/desktop-ebook-vm.service'
import { XAPIHelperService } from 'modules/foc/services/xAPI-helper.service';
import DesktopConfig from 'modules/desktop/services/config';
import { ApiGatewayHelperService } from 'modules/app/services/apigateway-helper.service';

import { appSetting } from 'config/appSettings';
import { environment } from 'environments/environment';
import { NetwordDetectionService } from 'modules/app/services/network-detection.service';
import { DownloadHelperService } from 'modules/desktop/services/download-helper.service';

declare var $:any;
import { HttpClient } from '@angular/common/http';

@Injectable()
export class StudentBookResolver  {
    constructor(        
        private studentBookService : StudentBookService,
        private appHelperService : AppHelperService,
        private authService: AuthService,
        private lastPageDataService : LastPageDataService,
        private desktopEBookVMService: DesktopEBookVMService,
        private xAPIHelperService: XAPIHelperService,
        private apiGatewayHelperService: ApiGatewayHelperService,
        private networkService: NetwordDetectionService,
        private downloadHelperService: DownloadHelperService,
        private http: HttpClient,

        ) { }

    resolve(route: ActivatedRouteSnapshot): Observable<any> | Promise<any> | any {
        return this.getStudentBook(route.params, route.queryParams);

    }

    async getStudentBook(params, queryParams){
        let hideContentAsRedirectImminent = false;
        let userProfileData = this.authService.getUserData();
        let bookDownloadInfo = this.downloadHelperService.isBookDownloaded(params.studentBookCode);

        let routePrefix = this.apiGatewayHelperService.getApiGatewayPrefixUrl();
        if(environment.isDesktopApp){
            routePrefix = DesktopConfig.app.api.baseUrl + routePrefix;
        }
        let serverRoutePrefix = routePrefix.substr(0, routePrefix.length - 1);

		this.apiGatewayHelperService.setSpaceId(params.spaceId);

        let data, notesData, dataNeeded;

        // fetch student book data and notes data in parallel.
        // this is a blocking operation and if the number of notes is large, this may take time.
        // as per cup, number of notes will be ~100 per book so this will not take extra time.
        // notes data is fetched here so that we are able to display note hotspots on book load.
        try {

            let promisesArray = [];
            promisesArray.push(this.studentBookService.getStudentBook(params.spaceId, params.productCode, params.studentBookCode, params.class, queryParams).toPromise());
            
            dataNeeded = {
                allSpaces: true, // return all spaces for both teacher and student
                allClasses:  !(userProfileData.role == 'student' || userProfileData.launchType == 'lti'), // return all classes from each space for teacher
                spaceSpecificClasses : params.class ? [params.spaceId] : [] // return all classes of this particular space Id if the user navigates to student book from a class
            }

            promisesArray.push(this.studentBookService.getUserSpacesAndClasses(params.spaceId, dataNeeded));

            if (!environment.isDesktopApp || (environment.isDesktopApp && !bookDownloadInfo)) {
                promisesArray.push(this.http.post(routePrefix + 'fetchNotes', { "studentBookCode": params.studentBookCode }).toPromise());
            }

            let promiseResponses = await Promise.all(promisesArray);

            if (!environment.isDesktopApp || (environment.isDesktopApp && !bookDownloadInfo)) {
                notesData = promiseResponses.pop();
            }

            data = {...promiseResponses.pop(), ...promiseResponses.pop()};
            //show error in case of empty response we get in case of particular lgid
            if(data?.lgidType1ItemData && Object.keys(data.lgidType1ItemData).length === 0) {
                this.appHelperService.navigateToOnboardingApp(['lti-onboarding-error', 'gradebookItemUrlInvalid']);
                return;
            }
        } catch (error) {
            console.log(error);
            if (environment.isDesktopApp) {

                let isOnline = await this.networkService.isOnline(true);

                let isAuthError = localStorage.getItem(`${appSetting.localStorage.foc}.AUTH_ERROR`);
                if (environment.isDesktopApp && (isAuthError == "true")) {
                    this.appHelperService.navigateToLoginPage(isOnline);
                } else {
                    this.appHelperService.hideLoaderAndNavigateToDashboardApp(params.class, params.spaceId, params.productCode);
                    // $('#assetErrorModal').attr('missing','meta');

                    if (isOnline) {
                        $('#assetErrorModal').attr('mode', 'online');
                        $('#assetErrorModal').modal('show');
                    } else {
                        $('#assetErrorModal').modal('show');
                    }
                }

                return;
            }
        }

        let spaces = data && data.userSpaces;
        // Check if user has the correct space.
        let spaceFound;
        if (spaces && spaces.entities) {
            // if current space in url matches one of the spaces user has.
            spaceFound = spaces.entities.find((space) => { return space.space_key === params.spaceId });
        }
        if (!spaceFound) {
            hideContentAsRedirectImminent = true;
            this.appHelperService.navigateToDashboardApp([]);
            return;
        }

        if (data.status && data.status === "error") {
            hideContentAsRedirectImminent = true;
            this.appHelperService.navigateToDashboardApp([]);
            return;
        }

        // if user is not a student, send data of all user classes
        if (dataNeeded.allClasses) {
            let classArray = [];
            spaces.entities.forEach((space) => {
                space?.classes?.entities.forEach(function (userClass) {
                    userClass.customSpaceKey = space.space_key
                    classArray.push(userClass);
                });
            });
            data.userClasses = {entities: classArray}
        }

        // if class is provided in params,
        // check if user belongs in the list of class users,
        // check if class belongs in the list of user classes,
        // check if the current class is a context based class.
        if (params.class) {

            let classDetails = data?.userClasses?.entities?.find(classs => { return classs.uuid == params.class });

            if (!classDetails) {
                hideContentAsRedirectImminent = true;
                this.appHelperService.navigateToDashboardApp([]);
                return;
            } else {
                data.classDetails = classDetails;
            }
            //
            // if the bundle does not exist in the class, then return back
            if (data && data.particularClass && data.particularClass.bundles && Object.keys(data.particularClass.bundles).indexOf(params.productCode) == -1) {
                hideContentAsRedirectImminent = true;
                this.appHelperService.navigateToDashboardApp([]);
                return;
            }

        }

        let isContextBasedClass;
        isContextBasedClass = false;
        if (data && data.classDetails && data.classDetails.analyticsharingmodel == "NOINHERIT_SYNCBACK") {
            console.log('context based class');
            isContextBasedClass = true;
        }
        

        const userRole = userProfileData.role === "student" ? "student" : "teacher";
        const productInfo = data.bundle.products.filter(product => product["dls-product"].code === params.studentBookCode)[0];

        // if the book does not exist in the bundle then redirect back
        if (!productInfo) {
            hideContentAsRedirectImminent = true;
            this.appHelperService.navigateToDashboardApp([]);
            return;
        }

        const isLtiTeacher = userProfileData.launchType == "lti" && userRole == 'teacher';
        // if the current book is expired, navigate back to dashboard
        if(!environment.isDesktopApp && !productInfo.activation.free[userRole]) {
            let entitlement = data.entitlements.entities.find(ent => ent.productcode === productInfo['dls-product'].code);
            if (!isLtiTeacher && entitlement && entitlement.entitlement.end_date < Date.now()) {
                hideContentAsRedirectImminent = true;
                this.appHelperService.showLoaderAndNavigateToDashboard(params.class, params.spaceId, params.productCode);
                return;
            }

            // expiry handling for pplus webapp
            // check if the currently launched book belongs to a pplus
            // if yes, check if any of the pplus books is expired.
            // if yes, then redirect back.

            let isPPlusBook = productInfo && productInfo.meta && (productInfo.meta['cup-pplus-component'] || productInfo.meta['pplus-component']);
            if (isPPlusBook && !isLtiTeacher) {
                let hasExpiredPPlusBook = false;
                data.bundle.products.forEach(product => {
                    if (product['ext-product'].type === 'imgbook' && product && product.meta && (product.meta['cup-pplus-component'] || product.meta['pplus-component'])) {
                        let foundEntitlement = data.entitlements.entities.find(ent => ent.productcode === product['dls-product'].code);
                        if (foundEntitlement && entitlement.entitlement.end_date < Date.now()) {
                            hasExpiredPPlusBook = true;
                        }
                    }
                });

                if (hasExpiredPPlusBook) {
                    hideContentAsRedirectImminent = true;
                    this.appHelperService.showLoaderAndNavigateToDashboard(params.class, params.spaceId, params.productCode);
                    return;
                }
            }

        }

        // if ebook is hidden or coming soon, redirect back to dashboard app
        // if space is mqa or visibilty is true(undefined for old products), then do not redirect
        if (!this.studentBookService.checkIfBookVisible(productInfo, params.spaceId, spaces)) {
            hideContentAsRedirectImminent = true;
            this.appHelperService.navigateToDashboardApp([]);
        }

        //instantiate xAPIAnalytics only once for a book launch
        this.xAPIHelperService.setXAPIAnalyticsObject({
            serverRoutePrefix: serverRoutePrefix,
            dlsProductCode: data.book.meta.code,
            spaceId: params.spaceId,
            userId: userProfileData.extUserId
        })

        if (environment.isDesktopApp) {
            // initialize VM service.
            await this.desktopEBookVMService.initializeVMService(userProfileData.extUserId, data.book.meta.code, params.spaceId, data.bundle['bundle-code'], bookDownloadInfo);
        }

        let lastPageData, entitiesObject;
        if(this.desktopEBookVMService.getActiveVM()) {
            
            lastPageData = this.desktopEBookVMService.getLocalData().preferencesData;

            if (lastPageData.length) {

                lastPageData = JSON.parse(lastPageData[0].data).data.lastPageLabel;
                let temp = this.desktopEBookVMService.getLocalData().preferencesData[0];

                entitiesObject = {
                    data: temp.data,
                    documentid: temp.documentId.substring(3),
                    folderid: temp.folderId,
                    user_modified: temp.timestamp
                }
            } else {
                lastPageData = undefined;


            }

            // getAllnotesData in case of downloaded book.
            let localVMData = this.desktopEBookVMService.getLocalData();
            notesData = {
                entities: localVMData.notesData,
                cursor: ""
            }
        } else {
            
            lastPageData = data && data.lastPageData && data.lastPageData.entities.length && JSON.parse(data.lastPageData.entities[0].data).data.lastPageLabel;
            entitiesObject = data && data.lastPageData && data.lastPageData.entities && data.lastPageData.entities[0];
        }

        this.lastPageDataService.setLastPageDataCache(params.studentBookCode, userProfileData.extUserId, params.spaceId, lastPageData, entitiesObject);

        // Temporary fix against an issue logged 401 to handle old ebooks which happens to have gated for teachers and free for learners
        if (productInfo.activation.gated.teacher) {
            productInfo.activation.gated.student = {};
        }

        if ((!isLtiTeacher && ((!productInfo.activation.free[userRole] && productInfo.activation.gated[userRole] && !data.entitlements.entities.some(entitlement => entitlement.productcode === params.studentBookCode) && !data?.schoolEntitlements?.entities.some(entitlement => entitlement.productcode === params.studentBookCode)) || (!productInfo.activation.free[userRole] && !productInfo.activation.gated[userRole] )))) {
            hideContentAsRedirectImminent = true;
            this.appHelperService.navigateToDashboardApp([]);
            return;
        }

        if (data.bundle && !environment.isDesktopApp) {
            data.bundle.cover = "";
            if (data.bundle.attribs && data.bundle.attribs.hasOwnProperty('cover-product-code')) {
                data.bundle.cover = data.bundle.path + '/' + data.bundle.attribs['cover-product-code'] + '/' + data.bundle.image;
            } else {
                let olpProduct;
                olpProduct = data.bundle.products.find((product) => product['ext-product'].type === 'OLP');
                if (!olpProduct || (olpProduct && olpProduct.meta['not-published'])) {
                    delete data.bundle.cover;
                } else {
                    if (data.bundle.image) {
                        data.bundle.cover = data.bundle.path + '/' + data.bundle.products.find((product) => product['ext-product'].type === 'OLP')['dls-product'].code + '/' + data.bundle.image;
                    } else {
                        delete data.bundle.cover;
                    }
                }
            }
        }
        // adding meta key in response object so as to pass the lastPage to student book.
        // lastPage is used to open the studentbook to page that was last visited by user.
        Object.assign(data, {meta: {
            spaceId: params.spaceId,
            lastPage: lastPageData,
            hideContentAsRedirectImminent: hideContentAsRedirectImminent,
            notesData: notesData,
            isContextBasedClass
        }});
        return data;
    }
}